import { Injectable, EventEmitter } from '@angular/core';
import { parseData } from '@ups/xplat/utils';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MyHttpClientFactory, environment } from '@ups/xplat/core';
import { EmployeeQueryResult, UserRegistrationResultDto } from '../../models';
import { IODataResult } from '@ups/xplat/api/dto';

export interface PacketRegistrationCheck {
  ActionString?: 'None' | 'Register' | 'Rehire';
  NeedsConfirmation?: boolean;
  HtmlMessage: string;
  /** An open packet from the last 24 h: Register handles it (returns it / retries login setup), so None does not block. */
  RecentOpenPacket?: boolean;
  /** Rehire status Caution (not expired), as a short plain note HR sees before registering (WI 34635). */
  CautionNote?: string | null;
}

/** WI 34635 slice 5: the details typed so far in the Register dialog (POST body: the SSN stays out of the URL). */
export interface PacketRegistrationWarningsRequest {
  Ssn: string;
  Email?: string;
  FirstName?: string;
  LastName?: string;
  DateOfBirth?: Date;
}

/**
 * Another person behind those details (WI 34635 slice 5): names only, and only of people the caller may see (their
 * Manage users grid, their PR Cos); never their SSN.
 */
export interface PacketRegistrationWarnings {
  /** Another Viewpoint employee holds the email: the export refuses it, so Register does too. */
  EmailInViewpoint?: boolean;
  /** Their name and HRRef, when they are in one of the caller's PR Cos. */
  EmailViewpointEmployee?: string | null;
  EmailViewpointHrRef?: number | null;
  /** Other packet users with this email; HR confirms it is shared. */
  EmailSharedCount: number;
  /** Up to three of them the caller's grid lists; the rest are only counted. */
  EmailSharedWith: string[];
  PossibleDuplicates: PacketRegistrationPossibleDuplicate[];
}

export interface PacketRegistrationPossibleDuplicate {
  /** 0 when the caller's Manage users grid does not list them (then no username or last four). */
  UserId: number;
  FullName: string;
  UserName?: string | null;
  SsnLast4?: string | null;
}

const GET_ALLEMPLOYEECHANGES = '/api/employeechanges/';
const GET_EMPLOYEECHANGES_BY_HRREF = '/api/employeechanges/{:hrref}';
const PUT_DECIDEEMPLOYEECHANGES_BY_HRREF =
  '/api/employeechanges/authorize/{:hrref}';
const PUT_EMPLOYEECHANGES_DATA_BY_HRREF =
  '/api/employeechanges/employeeaddress/{:hrref}';

// SPARTA
const GET_PREFERRED_SAFETY_COUNCILS = '/api/preferredSafetyCouncils/';

// PACKET
const GET_EMPLOYEE_PACKET_REGISTERED = '/api/employee/register/{:ssn}/check';
const PUT_EMPLOYEE_REGISTER = '/api/employee/register/';
const POST_EMPLOYEE_REGISTER_WARNINGS = '/api/employee/register/warnings';
const QUERY_EMPLOYEES = '/api/employee/odata-query';
const POST_RESEND_INVITE = '/api/employee/resend-invite/{:securityUserId}';

/**
 * Outcome of a resend-invite / reset attempt. These are the values the RESEND endpoint can return — a
 * subset of the SS API's `ResendInviteOutcome`, which also carries `Issued` and `UnknownApp` for the
 * issue-without-send endpoint (WI 34167) that no web caller reaches. `NotConfigured` is the packet API's own legacy
 * credentials outcome (WI 34891): the legacy sign-in address is not configured, so nothing was sent.
 */
export type ResendInviteOutcome =
  | 'Sent'
  | 'NotFound'
  | 'NotInternalAuth'
  | 'Inactive'
  | 'RateLimited'
  | 'AlreadySet'
  | 'NoEmail'
  | 'NotConfigured';

// PascalCase to match the packet API's default JSON casing for plain (non-OData) MVC
// responses — the same convention as `PacketRegistrationCheck` above.
export interface ResendInviteResult {
  Outcome: ResendInviteOutcome;
}

@Injectable({
  providedIn: 'root',
})
export class EmployeePortalService {
  employeesChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  private http: HttpClient;

  constructor(httpFactory: MyHttpClientFactory) {
    this.http = httpFactory.createHttpClient('', true);
  }

  public getAllEmployeeChanges(): Promise<unknown> {
    const url = GET_ALLEMPLOYEECHANGES;
    return this.http.get(url).toPromise().then(parseData);
  }

  public getEmployeeChangesByHrref(hrref: string): Promise<unknown> {
    const url = GET_EMPLOYEECHANGES_BY_HRREF.split('{:hrref}').join(hrref);
    return this.http.get(url).toPromise().then(parseData);
  }

  public getPreferredSafetyCouncils(): Promise<unknown> {
    const url = GET_PREFERRED_SAFETY_COUNCILS;
    return this.http.get(url).toPromise().then(parseData);
  }

  public putDecideEmployeeChangesByHrref(
    pendingAddress: unknown,
    hrref: string
  ): Promise<unknown> {
    const url =
      PUT_DECIDEEMPLOYEECHANGES_BY_HRREF.split('{:hrref}').join(hrref);

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers };

    return this.http
      .put(url, pendingAddress, options)
      .toPromise()
      .then(parseData);
  }

  public putEmployeeChangesDataByHrref(
    changes: unknown,
    hrref: string
  ): Promise<unknown> {
    const url = PUT_EMPLOYEECHANGES_DATA_BY_HRREF.split('{:hrref}').join(hrref);

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers };

    return this.http.put(url, changes, options).toPromise().then(parseData);
  }

  /** `prCo` (optional): the company being registered into, whose Viewpoint record supplies the Caution note. */
  public getEmployeePacketRegistered(
    ssn: string,
    prCo?: number
  ): Promise<PacketRegistrationCheck> {
    const path = GET_EMPLOYEE_PACKET_REGISTERED.split('{:ssn}').join(ssn);
    const url = prCo ? `${path}?prCo=${prCo}` : path;
    return this.http
      .get<PacketRegistrationCheck>(environment.urls.packetAPI + url)
      .toPromise()
      .then(parseData);
  }

  /** WI 34635 slice 5: shared-email and same-name-and-birth-date warnings for the Register dialog. */
  public postRegistrationWarnings(
    request: PacketRegistrationWarningsRequest
  ): Promise<PacketRegistrationWarnings> {
    return this.http
      .post<PacketRegistrationWarnings>(
        environment.urls.packetAPI + POST_EMPLOYEE_REGISTER_WARNINGS,
        request
      )
      .toPromise()
      .then(parseData);
  }

  // `prospectData` is intentionally `unknown` (matching the shape a legacy
  // manpower caller passes): the HR register dialog types its own payload as
  // `EmployeeDetailsDto` before calling, so type-safety lives at the call site
  // without forcing the loose legacy caller to change.
  // `client`: the HR app's Register dialog sends 'HrPacket' (WI 34635): the server then requires a Caution
  // acknowledgement even from a caller who also has Resource Allocation rights. RA's page sends nothing.
  public putRegisterEmployeeInPacket(
    prospectData: unknown,
    allowReRegistration: boolean,
    client?: 'HrPacket'
  ): Promise<UserRegistrationResultDto> {
    const url = PUT_EMPLOYEE_REGISTER;

    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    if (allowReRegistration) {
      headers = headers.append('Data-AllowReRegistration', 'true');
    }
    if (client) {
      headers = headers.append('Data-RegistrationClient', client);
    }

    const options = { headers };
    return this.http
      .put<UserRegistrationResultDto>(
        environment.urls.packetAPI + url,
        prospectData,
        options
      )
      .toPromise()
      .then(parseData);
  }

  /**
   * Re-sends the InternalAuth set-password email for an already-provisioned employee. With
   * `resetExisting` false (default) it only sends when no password was set yet (a lost/unused
   * invite); with `resetExisting` true it is an HR-initiated reset that sends even when a password
   * already exists. The packet API supplies the employee-portal returnTo; the returned outcome
   * tells HR whether it sent, and if not, why.
   */
  public resendInvite(
    securityUserId: string,
    resetExisting = false
  ): Promise<ResendInviteResult> {
    const url =
      POST_RESEND_INVITE.split('{:securityUserId}').join(securityUserId) +
      `?reset=${resetExisting}`;
    return this.http
      .post<ResendInviteResult>(environment.urls.packetAPI + url, null)
      .toPromise()
      .then(parseData) as Promise<ResendInviteResult>;
  }

  public queryPacketUsers(query: string) {
    return this.http.get<IODataResult<EmployeeQueryResult>>(
      environment.urls.packetAPI + QUERY_EMPLOYEES + '?$count=true&' + query
    );
  }

  setEmployeesChanged() {
    this.employeesChanged.emit(true);
  }

  public handleError(error: unknown) {
    return Promise.reject(error);
  }

  protected parseData(res: { body: string }): unknown {
    const body = JSON.parse(res.body, function (_key, value: unknown) {
      if (typeof value === 'string') {
        const match =
          /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)$/.exec(
            value
          );
        if (match) {
          return new Date(
            Date.UTC(
              +match[1],
              +match[2] - 1,
              +match[3],
              +match[4],
              +match[5],
              +match[6]
            )
          );
        }
      }
      return value;
    });
    return body;
  }
}
